import logger from '../lib/logger';

const IDLE_WARNING_TIME = 10 * 60 * 1000; // 10 minuter
const IDLE_LOGOUT_TIME = 15 * 60 * 1000; // 15 minuter
const ALLOWED_LOCALSTORAGE_KEYS = new Set(['theme', 'locale', 'tenant-id', 'demo-session', 'demo-session-version']);
const SENSITIVE_KEYS = new Set(['email', 'phone', 'personalId', 'internal_notes', 'body', 'password', 'token', 'service_role']);

let idleTimer: ReturnType<typeof setTimeout> | null = null;
let warningTimer: ReturnType<typeof setTimeout> | null = null;
let lastActivity = Date.now();
let onIdleWarning: (() => void) | null = null;
let onSessionLogout: (() => void) | null = null;

// Callbacks for clearing app state (selected customer, workspace tabs)
let clearSelectedCustomer: (() => void) | null = null;
let clearWorkspaceTabs: (() => void) | null = null;

const updateLastActivity = () => {
  lastActivity = Date.now();
  resetIdleTimers();
};

const resetIdleTimers = () => {
  if (idleTimer) clearTimeout(idleTimer);
  if (warningTimer) clearTimeout(warningTimer);

  warningTimer = setTimeout(() => {
    logger.audit('SESSION_IDLE_WARNING', { idleTime: IDLE_WARNING_TIME });
    if (onIdleWarning) onIdleWarning();
  }, IDLE_WARNING_TIME);

  idleTimer = setTimeout(() => {
    logger.audit('SESSION_IDLE_LOGOUT', { idleTime: IDLE_LOGOUT_TIME });
    secureLogout();
    if (onSessionLogout) onSessionLogout();
  }, IDLE_LOGOUT_TIME);
};

const secureLogout = () => {
  logger.audit('SECURE_LOGOUT_INITIATED', {});
  
  // Clear app state
  if (clearSelectedCustomer) {
    logger.audit('CLEARING_SELECTED_CUSTOMER', {});
    clearSelectedCustomer();
  }
  
  if (clearWorkspaceTabs) {
    logger.audit('CLEARING_WORKSPACE_TABS', {});
    clearWorkspaceTabs();
  }
  
  clearSensitiveStorage();
  
  if (idleTimer) clearTimeout(idleTimer);
  if (warningTimer) clearTimeout(warningTimer);
};

const clearSensitiveStorage = () => {
  sessionStorage.clear();
  Object.keys(localStorage).forEach(key => {
    if (!ALLOWED_LOCALSTORAGE_KEYS.has(key)) {
      localStorage.removeItem(key);
    }
  });
  validateNoPIIInStorage();
};

const validateNoPIIInStorage = () => {
  const allStorage = { ...localStorage, ...sessionStorage };
  Object.entries(allStorage).forEach(([key, value]) => {
    if (SENSITIVE_KEYS.has(key)) {
      logger.security('SENSITIVE_KEY_FOUND_IN_STORAGE', { key });
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    }
    try {
      const parsed = JSON.parse(value as string);
      if (typeof parsed === 'object') {
        Object.keys(parsed).forEach(k => {
          if (SENSITIVE_KEYS.has(k)) {
            logger.security('PII_FOUND_IN_STORED_OBJECT', { key, field: k });
            localStorage.removeItem(key);
            sessionStorage.removeItem(key);
          }
        });
      }
    } catch {
      // Not JSON, ignore
    }
  });
};

const initializeSession = (callbacks?: {
  onIdleWarning?: () => void;
  onSessionLogout?: () => void;
  clearSelectedCustomer?: () => void;
  clearWorkspaceTabs?: () => void;
}) => {
  logger.audit('SESSION_SECURITY_INITIALIZED', {});
  
  onIdleWarning = callbacks?.onIdleWarning || null;
  onSessionLogout = callbacks?.onSessionLogout || null;
  clearSelectedCustomer = callbacks?.clearSelectedCustomer || null;
  clearWorkspaceTabs = callbacks?.clearWorkspaceTabs || null;

  const activityEvents = ['mousemove', 'keydown', 'click', 'scroll'];
  activityEvents.forEach(event => {
    window.addEventListener(event, updateLastActivity);
  });

  window.addEventListener('beforeunload', () => {
    clearSensitiveStorage();
    if (idleTimer) clearTimeout(idleTimer);
    if (warningTimer) clearTimeout(warningTimer);
  });

  resetIdleTimers();
  validateNoPIIInStorage();
};

const cleanupSession = () => {
  logger.audit('SESSION_CLEANUP', {});
  const activityEvents = ['mousemove', 'keydown', 'click', 'scroll'];
  activityEvents.forEach(event => {
    window.removeEventListener(event, updateLastActivity);
  });
  window.removeEventListener('beforeunload', clearSensitiveStorage);
  if (idleTimer) clearTimeout(idleTimer);
  if (warningTimer) clearTimeout(warningTimer);
};

export default {
  initializeSession,
  cleanupSession,
  secureLogout,
  validateNoPIIInStorage,
};
